import React, { useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaMicrophone, FaSearch } from "react-icons/fa";
import {
  aramaMetni,
  dizinYukle,
  kategoriAdi,
  kategoriAdlariYukle,
  urunleriYukle,
  urunYolu,
} from "../data/katalog";
import { akilliAra } from "../data/akilliArama";

// Sesli arama: Chrome, Edge, Safari ve Android'de var; desteklemeyen tarayıcıda mikrofon düğmesi hiç görünmez
const SesTanima = typeof window !== "undefined" ? window.SpeechRecognition || window.webkitSpeechRecognition : null;
const SES_DILI = { tr: "tr-TR", en: "en-US", fr: "fr-FR", ar: "ar-SA" };

const URUN_SINIRI = 6;
const KATEGORI_SINIRI = 4;

/**
 * Navbar arama kutusu — yazarken ürün ve kategori önerileri açılır.
 * Katalog, kutuya ilk odaklanıldığında yüklenir (her sayfa açılışında indirilmez).
 * Enter: seçili öneriyi açar; öneri seçili değilse tüm sonuçlar sayfasına gider.
 */
export default function CanliArama({ ekSinif = "", onSec }) {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || "tr").split("-")[0];
  const navigate = useNavigate();
  const kutuRef = useRef(null);

  const [arama, setArama] = useState("");
  const [acik, setAcik] = useState(false);
  const [secili, setSecili] = useState(-1);
  const [veri, setVeri] = useState(null); // { dizin: [{u, m}], adlar, dizinHarita }
  const gecikmeli = useDeferredValue(arama);
  const [dinliyor, setDinliyor] = useState(false);
  const [sesHatasi, setSesHatasi] = useState("");
  const tanimaRef = useRef(null);

  const yukle = () => {
    if (veri) return;
    Promise.all([urunleriYukle(), kategoriAdlariYukle(), dizinYukle()]).then(([urunler, adlar, dizinHarita]) =>
      setVeri({ urunler, adlar, dizinHarita })
    );
  };

  // Sayfadan çıkılırsa mikrofonu kapat
  useEffect(() => () => tanimaRef.current?.abort(), []);

  const sesliAra = () => {
    if (dinliyor) {
      tanimaRef.current?.stop();
      return;
    }
    const tanima = new SesTanima();
    tanima.lang = SES_DILI[lang] || "tr-TR";
    tanima.interimResults = true; // konuşurken yazı kutuda belirsin
    tanima.maxAlternatives = 1;
    tanima.onresult = (e) => {
      const metin = Array.from(e.results)
        .map((r) => r[0].transcript)
        .join(" ")
        .replace(/[.!?。]+$/, "")
        .trim();
      setArama(metin);
      setAcik(true);
    };
    tanima.onerror = (e) => {
      setDinliyor(false);
      if (e.error === "not-allowed" || e.error === "service-not-allowed") setSesHatasi(t("search.micDenied"));
      else if (e.error === "no-speech") setSesHatasi(t("search.micNoSpeech"));
      setTimeout(() => setSesHatasi(""), 5000);
    };
    tanima.onend = () => setDinliyor(false);
    tanimaRef.current = tanima;
    yukle();
    setSesHatasi("");
    setArama("");
    try {
      tanima.start();
      setDinliyor(true);
    } catch {
      setDinliyor(false);
    }
  };

  // Dışarı tıklanınca kapan
  useEffect(() => {
    const kapat = (e) => {
      if (kutuRef.current && !kutuRef.current.contains(e.target)) setAcik(false);
    };
    document.addEventListener("mousedown", kapat);
    return () => document.removeEventListener("mousedown", kapat);
  }, []);

  const sonuc = useMemo(() => {
    const q = aramaMetni(gecikmeli);
    if (!veri || q.length < 2) return { urunler: [], kategoriler: [], toplam: 0, duzeltme: null };
    const { sonuclar, duzeltme } = akilliAra(veri.urunler, gecikmeli);

    // Kategoriler: adı sorguyu (ya da düzeltilmiş halini) içeren, gerçekten sayfası olan kategoriler
    const kelimeler = aramaMetni(duzeltme || gecikmeli).split(/\s+/);
    const kategoriler = Object.keys(veri.adlar || {})
      .filter((slug) => veri.dizinHarita && slug in veri.dizinHarita)
      .map((slug) => ({ slug, ad: kategoriAdi(veri.adlar, slug, lang) }))
      .filter((k) => kelimeler.every((kel) => aramaMetni(k.ad).includes(kel)))
      .slice(0, KATEGORI_SINIRI);

    return { urunler: sonuclar.slice(0, URUN_SINIRI), kategoriler, toplam: sonuclar.length, duzeltme };
  }, [gecikmeli, veri, lang]);

  // Klavyeyle gezilebilen öneriler: önce kategoriler, sonra ürünler
  const ogeler = [
    ...sonuc.kategoriler.map((k) => ({ tip: "k", yol: `/urunler/${k.slug}`, state: { baslik: k.ad }, veri: k })),
    ...sonuc.urunler.map((u) => ({ tip: "u", yol: urunYolu(u), veri: u })),
  ];

  useEffect(() => setSecili(-1), [gecikmeli]);

  const git = (yol, state) => {
    navigate(yol, state ? { state } : undefined);
    setAcik(false);
    setArama("");
    onSec?.();
  };

  const gonder = (e) => {
    e.preventDefault();
    if (secili >= 0 && ogeler[secili]) return git(ogeler[secili].yol, ogeler[secili].state);
    const q = arama.trim();
    git(q ? `/urunler?q=${encodeURIComponent(q)}` : "/urunler");
  };

  const tus = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setAcik(true);
      setSecili((s) => Math.min(s + 1, ogeler.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSecili((s) => Math.max(s - 1, -1));
    } else if (e.key === "Escape") {
      setAcik(false);
    }
  };

  const panelGoster = acik && aramaMetni(arama).length >= 2;
  const yukleniyor = panelGoster && !veri;

  return (
    <div ref={kutuRef} className={`relative ${ekSinif}`}>
      <form
        onSubmit={gonder}
        role="search"
        className="flex w-full items-stretch overflow-hidden rounded-md border-2 border-ink bg-white"
      >
        <label className="flex flex-1 items-center gap-3 pl-4">
          <FaSearch size={14} className="shrink-0 text-ink" aria-hidden="true" />
          <span className="sr-only">{t("navbar.searchPlaceholder")}</span>
          <input
            type="search"
            value={arama}
            onChange={(e) => {
              setArama(e.target.value);
              setAcik(true);
              setSesHatasi("");
            }}
            onFocus={() => {
              yukle();
              setAcik(true);
            }}
            onKeyDown={tus}
            placeholder={dinliyor ? t("search.listening") : t("navbar.searchPlaceholder")}
            autoComplete="off"
            role="combobox"
            aria-expanded={panelGoster}
            aria-controls="canli-arama-listesi"
            aria-activedescendant={secili >= 0 ? `canli-arama-${secili}` : undefined}
            className="h-12 w-full min-w-0 bg-transparent text-[14px] text-ink outline-none placeholder:text-muted"
          />
        </label>
        {SesTanima && (
          <button
            type="button"
            onClick={sesliAra}
            aria-label={dinliyor ? t("search.micStop") : t("search.mic")}
            aria-pressed={dinliyor}
            title={dinliyor ? t("search.micStop") : t("search.mic")}
            className={`relative flex w-11 shrink-0 items-center justify-center transition-colors ${
              dinliyor ? "text-rose-600" : "text-muted hover:text-ink"
            }`}
          >
            {dinliyor && <span aria-hidden="true" className="absolute h-8 w-8 animate-ping rounded-full bg-rose-500/25" />}
            <FaMicrophone size={15} className="relative" />
          </button>
        )}
        <button
          type="submit"
          className="px-5 text-[14px] font-semibold tracking-wide text-ink transition-colors hover:bg-ink hover:text-white"
        >
          {t("navbar.search")}
        </button>
      </form>

      {sesHatasi && !panelGoster && (
        <p role="status" className="absolute inset-x-0 top-full z-50 mt-2 rounded-lg border border-line bg-white px-4 py-2.5 text-[12.5px] text-body shadow-pop">
          {sesHatasi}
        </p>
      )}

      {panelGoster && (
        <div
          id="canli-arama-listesi"
          role="listbox"
          className="absolute inset-x-0 top-full z-50 mt-2 max-h-[70vh] overflow-y-auto rounded-xl border border-line bg-white text-start shadow-[0_24px_60px_-20px_rgba(0,0,0,0.35)]"
        >
          {yukleniyor && (
            <p className="px-4 py-5 text-[13px] text-muted">{t("navbar.searching")}</p>
          )}

          {sonuc.duzeltme && (
            <div className="border-b border-line bg-canvas/60 px-4 py-2.5 text-[13px] text-body">
              {t("search.showingFor")}{" "}
              <button type="button" onClick={() => setArama(sonuc.duzeltme)} className="font-semibold text-accent hover:underline">
                {sonuc.duzeltme}
              </button>
            </div>
          )}

          {!yukleniyor && ogeler.length === 0 && (
            <p className="px-4 py-5 text-[13px] text-muted">{t("shop.searchNoResult")}</p>
          )}

          {sonuc.kategoriler.length > 0 && (
            <div className="border-b border-line px-2 py-2">
              <p className="px-2 pb-1 pt-1 text-[10.5px] font-semibold uppercase tracking-eyebrow text-muted">
                {t("navbar.suggestCategories")}
              </p>
              {sonuc.kategoriler.map((k, i) => (
                <Link
                  key={k.slug}
                  id={`canli-arama-${i}`}
                  role="option"
                  aria-selected={secili === i}
                  to={`/urunler/${k.slug}`}
                  state={{ baslik: k.ad }}
                  onClick={(e) => {
                    e.preventDefault();
                    git(`/urunler/${k.slug}`, { baslik: k.ad });
                  }}
                  className={`flex items-center justify-between rounded-md px-2 py-2 text-[13.5px] text-ink transition-colors ${
                    secili === i ? "bg-canvas" : "hover:bg-canvas"
                  }`}
                >
                  <span>{k.ad}</span>
                  <span aria-hidden="true" className="arrow-forward text-muted" />
                </Link>
              ))}
            </div>
          )}

          {sonuc.urunler.length > 0 && (
            <div className="px-2 py-2">
              <p className="px-2 pb-1 pt-1 text-[10.5px] font-semibold uppercase tracking-eyebrow text-muted">
                {t("navbar.suggestProducts")}
              </p>
              {sonuc.urunler.map((u, j) => {
                const i = sonuc.kategoriler.length + j;
                return (
                  <Link
                    key={u.id}
                    id={`canli-arama-${i}`}
                    role="option"
                    aria-selected={secili === i}
                    to={urunYolu(u)}
                    onClick={(e) => {
                      e.preventDefault();
                      git(urunYolu(u));
                    }}
                    className={`flex items-center gap-3 rounded-md px-2 py-2 transition-colors ${
                      secili === i ? "bg-canvas" : "hover:bg-canvas"
                    }`}
                  >
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-md border border-line bg-white">
                      <img src={u.gorsel} alt="" loading="lazy" className="h-full w-full object-contain p-1" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[13.5px] font-medium text-ink">{u.ad}</span>
                      <span className="block truncate text-[12px] text-muted">
                        {u.marka} · {kategoriAdi(veri?.adlar, u.kategori, lang)}
                      </span>
                    </span>
                  </Link>
                );
              })}
            </div>
          )}

          {sonuc.toplam > 0 && (
            <button
              type="button"
              onClick={() => git(`/urunler?q=${encodeURIComponent(sonuc.duzeltme || arama.trim())}`)}
              className="flex w-full items-center justify-center gap-2 border-t border-line bg-canvas/60 px-4 py-3 text-[13px] font-semibold text-ink transition-colors hover:bg-canvas"
            >
              {t("navbar.seeAllResults", { count: sonuc.toplam })}
              <span aria-hidden="true" className="arrow-forward" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
